import { useEffect, useState } from "react";
import api, { apiError } from "../lib/api";
import { toast } from "sonner";
import { Smartphone, ShieldCheck, ShieldOff, Copy } from "lucide-react";

const inputCls = "w-full bg-[#0B0E17] border border-[#2A364F] rounded-md px-3 py-2 text-sm font-mono outline-none focus:border-[#3B82F6]";

export const TotpSection = () => {
  const [st, setSt] = useState(null);
  const [setup, setSetup] = useState(null);
  const [code, setCode] = useState("");
  const [recovery, setRecovery] = useState(null);
  const [busy, setBusy] = useState(false);
  const load = () => api.get("/auth/totp/status").then((r) => setSt(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const begin = async () => { setBusy(true); try { const { data } = await api.post("/auth/totp/setup"); setSetup(data); setCode(""); } catch (e) { toast.error(apiError(e)); } setBusy(false); };
  const enable = async () => {
    setBusy(true);
    try { const { data } = await api.post("/auth/totp/enable", { code }); setRecovery(data.recovery_codes); setSetup(null); setCode(""); toast.success("Google Authenticator etkin — artık her girişte kod istenecek"); load(); }
    catch (e) { toast.error(apiError(e)); }
    setBusy(false);
  };
  const disable = async () => {
    if (!window.confirm("Google Authenticator devre dışı bırakılsın mı? Giriş yalnızca şifre + yapboz ile yapılır.")) return;
    setBusy(true);
    try { await api.post("/auth/totp/disable", { code }); setCode(""); toast.success("Devre dışı bırakıldı"); load(); } catch (e) { toast.error(apiError(e)); }
    setBusy(false);
  };

  return (
    <section data-testid="totp-section" className="bg-[#121724] border border-[#1E2738] rounded-lg p-5 space-y-4">
      <div>
        <h3 className="text-sm font-bold flex items-center gap-2"><Smartphone className="h-4 w-4 text-[#3B82F6]" /> Giriş Güvenliği — Google Authenticator (TOTP)</h3>
        <p className="text-xs text-[#64748B] mt-1">Etkinleştirildiğinde giriş: e-posta + şifre + yapboz → ardından uygulamadaki 6 haneli kod. Kurtarma kodlarını güvenli bir yerde saklayın; telefonunuzu kaybederseniz tek giriş yolunuz onlardır.</p>
      </div>

      {st && (
        <div data-testid="totp-status" className={`flex items-center justify-between rounded-md px-4 py-3 border ${st.enabled ? "border-[#10B981]/40 bg-[#10B981]/10" : "border-[#F59E0B]/40 bg-[#F59E0B]/10"}`}>
          <div className="flex items-center gap-2 text-sm font-semibold">
            {st.enabled ? <ShieldCheck className="h-4 w-4 text-[#10B981]" /> : <ShieldOff className="h-4 w-4 text-[#F59E0B]" />}
            <span className={st.enabled ? "text-[#10B981]" : "text-[#F59E0B]"}>{st.enabled ? "ETKİN" : "KAPALI"}</span>
            {st.enabled && <span className="text-xs text-[#64748B] font-normal">· {st.recovery_codes_left} kurtarma kodu kaldı</span>}
          </div>
          {!st.enabled && !setup && <button data-testid="totp-setup-button" onClick={begin} disabled={busy} className="text-xs bg-[#3B82F6] hover:bg-[#2563EB] text-white rounded-md px-3 py-1.5 disabled:opacity-50">Kurulumu Başlat</button>}
        </div>
      )}

      {setup && (
        <div data-testid="totp-setup-box" className="grid sm:grid-cols-[180px_1fr] gap-4 items-start">
          <img src={setup.qr} alt="QR" className="w-[180px] h-[180px] rounded-md border border-[#1E2738] bg-white" />
          <div className="space-y-3 text-xs">
            <p className="text-[#94A3B8]">1) Google Authenticator'ı açın → <b>+</b> → <b>QR kodu tara</b>. Tarayamıyorsanız anahtarı elle girin:</p>
            <div className="flex items-center gap-2"><code data-testid="totp-secret" className="font-mono text-[#F1F5F9] bg-[#0B0E17] border border-[#1E2738] rounded px-2 py-1 break-all">{setup.secret}</code>
              <button type="button" onClick={() => { navigator.clipboard?.writeText(setup.secret); toast.success("Kopyalandı"); }} className="text-[#64748B] hover:text-[#F1F5F9]"><Copy className="h-3.5 w-3.5" /></button></div>
            <p className="text-[#94A3B8]">2) Uygulamada görünen 6 haneli kodu girip etkinleştirin:</p>
            <div className="flex gap-2">
              <input data-testid="totp-enable-code" inputMode="numeric" value={code} onChange={(e) => setCode(e.target.value)} placeholder="123456" className={inputCls} />
              <button data-testid="totp-enable-button" onClick={enable} disabled={busy || code.replace(/\s/g, "").length !== 6} className="shrink-0 text-xs bg-[#10B981] hover:bg-[#059669] text-[#0B0E17] font-semibold rounded-md px-3 disabled:opacity-50">Etkinleştir</button>
              <button onClick={() => setSetup(null)} className="shrink-0 text-xs text-[#64748B] hover:text-[#F1F5F9] px-2">İptal</button>
            </div>
          </div>
        </div>
      )}

      {recovery && (
        <div data-testid="totp-recovery-codes" className="rounded-md border border-[#F59E0B]/50 bg-[#0B0E17] p-4 space-y-2">
          <p className="text-xs font-semibold text-[#F59E0B]">Kurtarma kodları — yalnızca ŞİMDİ gösterilir, her biri bir kez kullanılır. Kaydedin!</p>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 font-mono text-sm text-[#F1F5F9]">{recovery.map((c) => <div key={c} className="bg-[#121724] border border-[#1E2738] rounded px-2 py-1 text-center">{c}</div>)}</div>
          <div className="flex gap-2">
            <button onClick={() => { navigator.clipboard?.writeText(recovery.join("\n")); toast.success("Kodlar kopyalandı"); }} className="text-xs text-[#3B82F6] hover:underline">Tümünü kopyala</button>
            <button onClick={() => setRecovery(null)} className="text-xs text-[#64748B] hover:text-[#F1F5F9]">Kaydettim, kapat</button>
          </div>
        </div>
      )}

      {st?.enabled && (
        <div className="flex gap-2 items-end">
          <div className="flex-1 space-y-1">
            <label className="text-[11px] text-[#64748B]">Devre dışı bırakmak için güncel kod (veya kurtarma kodu)</label>
            <input data-testid="totp-disable-code" value={code} onChange={(e) => setCode(e.target.value)} placeholder="123456" className={inputCls} />
          </div>
          <button data-testid="totp-disable-button" onClick={disable} disabled={busy || code.trim().length < 6} className="text-xs bg-[#EF4444]/15 border border-[#EF4444]/40 text-[#EF4444] rounded-md px-3 py-2 disabled:opacity-50">Devre Dışı Bırak</button>
        </div>
      )}
    </section>
  );
};
